/* Yönetici şifreleri — PBKDF2-HMAC-SHA256 (plan §11).

   Argon2id daha iyi bir seçim olurdu ama Workers'ta WASM gerektiriyor;
   PBKDF2 Web Crypto'nun içinde, yani hem Worker'da hem Node'da ek bağımlılık
   olmadan aynı çalışıyor. Bedeli iterasyon sayısıyla ödenir.

   Biçim tek bir dizede taşınır:

       pbkdf2$<iterasyon>$<tuz-b64url>$<özet-b64url>

   Parametreleri özetin yanında taşımak, ileride iterasyon sayısını
   yükseltmeyi mümkün kılar: eski satırlar kendi sayılarıyla doğrulanmaya
   devam eder, yeni satırlar yenisini alır. Sayıyı koda gömmek, yükseltme
   gününde bütün şifreleri kullanılamaz yapardı.

   Doğrulama sabit sürede yapılır (`equalBytes`). Şifre özetlerinde bu,
   imzalardaki kadar kritik değildir — saldırgan özeti değil şifreyi
   arıyor — ama karşılaştırmanın erken çıkması, aynı kodun başka bir yerde
   kullanılması hâlinde sessiz bir kusur bırakır. */

const enc = new TextEncoder();

/**
 * Workers'ın kabul ettiği en yüksek iterasyon sayısı — ve bizim kullandığımız.
 *
 * **Bu bir tercih değil, platform sınırı.** Cloudflare Workers'ın Web Crypto
 * uygulaması 100 000'in üstünü reddediyor:
 *
 *     NotSupportedError: Pbkdf2 failed: iteration counts above 100000
 *     are not supported (requested 600000).
 *
 * Bu, üretimde giriş ucu 500 dönerek öğrenildi; Node'da aynı sınır yok, o
 * yüzden yerelde ve testlerde 600 000 sorunsuz çalışıyordu. İki ortamın
 * ayrıldığı yer tam olarak burası.
 *
 * OWASP'ın PBKDF2-HMAC-SHA256 için verdiği bugünkü asgari 600 000, yani
 * altıda birindeyiz ve bunu olduğu gibi söylemek gerekir. Açığı kapatan
 * şeyler:
 *
 *   - **Şifreler üretiliyor, seçilmiyor.** `admin-user` komutu 24 base64url
 *     karakteri (~143 bit) veriyor. Bu entropide iterasyon sayısı pratikte
 *     anlamsızlaşır — kaba kuvvetin maliyetini belirleyen şey şifrenin
 *     kendisidir, özetin yavaşlığı değil. İterasyon sayısı, zayıf ve
 *     tekrar kullanılmış şifreleri korumak için var; burada öyle bir şifre
 *     yok.
 *   - **Yönetici sayısı bir elin parmağı kadar** ve giriş ucu IP başına on
 *     beş dakikada on denemeyle sınırlı (lib/ratelimit.ts).
 *   - **Access** yapılandırıldığında giriş ucuna ulaşmak bile ayrı bir
 *     kimlik doğrulaması gerektiriyor.
 *
 * Argon2id'ye geçmek sınırı tamamen aşardı ama WASM gerektiriyor; o gün
 * gelirse `hashPassword`/`verifyPassword` imzaları değişmeden kalır, çünkü
 * biçim parametreleri kendi içinde taşıyor.
 */
export const MAX_PBKDF2_ITERATIONS = 100_000;

/** Yeni özetlerin iterasyon sayısı. Platform sınırıyla aynı. */
export const PBKDF2_ITERATIONS = MAX_PBKDF2_ITERATIONS;

/** SHA-256 çıktısıyla aynı: 32 bayt. Daha kısası özeti boşa kırpardı. */
const KEY_LENGTH_BITS = 256;
const SALT_BYTES = 16;

function b64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function unb64url(s: string): Uint8Array<ArrayBuffer> {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + pad);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

async function derive(
  password: string,
  /* `Uint8Array<ArrayBuffer>`, çıplak `Uint8Array` değil: ikincisi
     `ArrayBufferLike` demek ve SharedArrayBuffer'ı da kapsıyor, Web Crypto
     ise onu kabul etmiyor. signing.ts ile aynı biçim. */
  salt: Uint8Array<ArrayBuffer>,
  iterations: number
): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, [
    'deriveBits'
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations },
    key,
    KEY_LENGTH_BITS
  );
  return new Uint8Array(bits);
}

/** Uzunluk farkını da sızdırmayan bayt karşılaştırması. */
function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  /* Uzunluk eşitliği gizlenemez (dizinin boyu zaten belli) ama erken dönüş
     yine de tek bir yere toplanır. */
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= (a[i] as number) ^ (b[i] as number);
  return diff === 0;
}

/**
 * Şifreyi saklanabilir biçime çevirir.
 *
 * Tuz her çağrıda yenidir: aynı şifreyi kullanan iki yönetici satırı
 * birbirine benzemesin, ve hazır tablo (rainbow) saldırısı işe yaramasın.
 */
export async function hashPassword(
  password: string,
  iterations: number = PBKDF2_ITERATIONS
): Promise<string> {
  if (password.length === 0) throw new Error('Boş şifre özetlenemez.');
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const hash = await derive(password, salt, iterations);
  return `pbkdf2$${iterations}$${b64url(salt)}$${b64url(hash)}`;
}

/**
 * Saklanan özetle şifreyi karşılaştırır.
 *
 * Bozuk ya da tanınmayan biçim `false` döner, fırlatmaz: veritabanındaki tek
 * bir bozuk satır giriş ucunu 500'e düşürmesin — o satırın sahibi giremez,
 * kalan herkes girebilir. Sessiz kalmaz ama: sebebi günlüğe yazılır.
 */
export async function verifyPassword(password: string, stored: string): Promise<boolean> {
  const parts = stored.split('$');
  if (parts.length !== 4 || parts[0] !== 'pbkdf2') {
    console.error('[şifre] tanınmayan özet biçimi — satır doğrulanamıyor.');
    return false;
  }
  const iterations = Number(parts[1]);
  if (!Number.isInteger(iterations) || iterations < 1) {
    console.error('[şifre] özetteki iterasyon sayısı geçersiz.');
    return false;
  }
  if (iterations > MAX_PBKDF2_ITERATIONS) {
    /* Sınırın **üstündeki** özet doğrulanamaz: Workers `deriveBits`'i
       reddediyor, yani bu satırın sahibi hiçbir şifreyle giremez. Sessizce
       `false` dönmek "şifre yanlış" gibi görünür ve saatler kaybettirir, o
       yüzden yapılacak iş adıyla yazılıyor. Bu hâl yalnızca Node'da
       üretilmiş (sınırsız) bir özetin Worker'a taşınmasıyla oluşur. */
    console.error(
      `[şifre] özet ${iterations} iterasyonla üretilmiş; Workers en çok ` +
        `${MAX_PBKDF2_ITERATIONS} destekliyor. Bu satır doğrulanamaz — ` +
        'şifreyi `npm run db:admin-user -- --email <adres> --force` ile sıfırlayın.'
    );
    return false;
  }
  let salt: Uint8Array<ArrayBuffer>;
  let expected: Uint8Array<ArrayBuffer>;
  try {
    salt = unb64url(parts[2] as string);
    expected = unb64url(parts[3] as string);
  } catch {
    console.error('[şifre] özetteki base64url çözülemedi.');
    return false;
  }
  if (password.length === 0) return false;
  const actual = await derive(password, salt, iterations);
  return equalBytes(actual, expected);
}

/**
 * Özetin bugünkü parametrelerle üretilip üretilmediği.
 *
 * Girişte `true` dönerse, şifre elimizdeyken yeniden özetlenip satır
 * güncellenebilir — iterasyon sayısını yükseltmenin tek sancısız yolu budur,
 * çünkü ham şifre yalnızca o anda mevcuttur.
 */
export function needsRehash(stored: string, iterations: number = PBKDF2_ITERATIONS): boolean {
  const parts = stored.split('$');
  if (parts.length !== 4 || parts[0] !== 'pbkdf2') return true;
  const stored_iterations = Number(parts[1]);
  if (!Number.isInteger(stored_iterations)) return true;
  /* Eşit değilse yenilenmeli — yalnızca "daha az" değil. Fazla iterasyonlu
     bir özet Workers'ta hiç doğrulanamıyor (yukarıdaki sınır), yani "daha
     güçlü olduğu için bırakılabilir" varsayımı burada tam tersine dönüyor:
     o satır kullanılamaz hâldedir ve yenilenmesi gerekir. */
  return stored_iterations !== iterations;
}
